import {
  CameraIcon,
  CheckIcon,
  ChevronDownIcon,
  ExitIcon,
  GearIcon,
} from '@radix-ui/react-icons';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Divider from '../../view/components/Divider';
import { DropdownMenu } from '../../view/components/DropdownMenu';
import SidebarTrigger from '../../view/components/SidebarTrigger';
import ThemeToggle from '../../view/components/ThemeToggle';
import { HeaderLayoutContext } from '../context/HeaderLayoutContext';
import { useAuth } from '../hooks/useAuth';
import { useWindowSize } from '../hooks/useWindowSize';
import { pagePath } from '../utils/consts/pagePath';

export default function HeaderLayout() {
  const { isMobile } = useWindowSize();
  const { title, actions } = useContext(HeaderLayoutContext);
  const { loggedInUser, tenant, signOut, tenantSignIn } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (isMobile) return;

  const firstName = loggedInUser?.name.trim().split(' ')[0];

  return (
    <header className="flex h-12 w-full items-center justify-between gap-2 px-2">
      <div className="flex min-w-0 items-center">
        <SidebarTrigger />
        <Divider vertical className="mx-2 h-5" />
        {title && (
          <h1 className="text-text-primary truncate text-xl font-medium">
            {title}
          </h1>
        )}
      </div>
      <div className="flex items-center gap-3">
        {firstName && (
          <span className="text-text-secondary hidden text-sm md:inline">
            {t('sidenavbar.header.greeting', { name: firstName })}
          </span>
        )}
        {tenant?.name && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <button
                type="button"
                aria-label={t('sidenavbar.header.userMenu')}
                className="text-text-brand hover:bg-hover-subtle flex max-w-40 items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-bold outline-none lg:max-w-56"
              >
                <CameraIcon className="size-4 shrink-0" aria-hidden />
                <span className="truncate">{tenant.name}</span>
                <ChevronDownIcon
                  className="size-4 shrink-0 opacity-70"
                  aria-hidden
                />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content side="bottom" align="end" sideOffset={4}>
              {loggedInUser && loggedInUser.memberships.length > 1 && (
                <>
                  {loggedInUser.memberships.map(
                    ({ tenant: membershipTenant }) => {
                      const isCurrent = membershipTenant.id === tenant?.id;
                      return (
                        <DropdownMenu.Item
                          key={membershipTenant.id}
                          disabled={isCurrent || !membershipTenant.active}
                          onClick={() => tenantSignIn(membershipTenant)}
                          className="text-text-primary flex items-center gap-2"
                        >
                          <CameraIcon className="size-4 shrink-0" aria-hidden />
                          <span className="flex-1 truncate">
                            {membershipTenant.name}
                          </span>
                          {isCurrent && (
                            <CheckIcon
                              className="size-4 shrink-0"
                              aria-hidden
                            />
                          )}
                        </DropdownMenu.Item>
                      );
                    },
                  )}
                  <DropdownMenu.Separator />
                </>
              )}
              <DropdownMenu.Item
                onClick={() => navigate(pagePath.settings)}
                className="flex items-center gap-2"
              >
                <GearIcon className="size-4 shrink-0" aria-hidden />
                {t('sidenavbar.footer.settings')}
              </DropdownMenu.Item>
              <DropdownMenu.Separator />
              <DropdownMenu.Item
                danger
                onClick={signOut}
                className="flex items-center gap-2"
              >
                <ExitIcon className="size-4 shrink-0" aria-hidden />
                {t('sidenavbar.footer.logout')}
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        )}
        {actions}
        <ThemeToggle />
      </div>
    </header>
  );
}
